import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reputationService, profitabilityService } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  TrendingUp,
  FileText,
  Bandage,
  AlertTriangle,
  CheckCircle2,
  Plus,
  Users,
  Calendar,
  Settings,
  LogOut,
  FileCheck,
  Globe,
  Download,
  Euro,
  Target,
  Award,
} from 'lucide-react';

const Dashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [missionTracking, setMissionTracking] = useState(null);
  const [upsellSuggestion, setUpsellSuggestion] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const navigate = useNavigate();

  const loadProfiles = useCallback(async () => {
    try {
      const data = await reputationService.getProfiles();
      setProfiles(data);
      if (data.length > 0) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        setSelectedProfile(data[0]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDashboardStats = useCallback(async (profileId) => {
    try {
      const stats = await reputationService.getDashboard(profileId);
      setDashboardStats(stats);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading dashboard stats:', error);
    }
  }, []);

  const loadMissionTracking = useCallback(async (profileId) => {
    try {
      const tracking = await profitabilityService.getMissionTracking(profileId);
      setMissionTracking(tracking);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading mission tracking:', error);
    }
  }, []);

  const loadUpsellSuggestions = useCallback(async (profileId) => {
    try {
      const suggestion = await profitabilityService.getUpsellSuggestions(profileId);
      if (suggestion.has_suggestion) {
        setUpsellSuggestion(suggestion);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading upsell suggestions:', error);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (selectedProfile) {
      loadDashboardStats(selectedProfile.id);
      loadMissionTracking(selectedProfile.id);
      loadUpsellSuggestions(selectedProfile.id);
    }
  }, [selectedProfile, loadDashboardStats, loadMissionTracking, loadUpsellSuggestions]);

  const handleDownloadPDF = async () => {
    if (!selectedProfile) return;
    
    setDownloadingPDF(true);
    try {
      const blob = await profitabilityService.downloadMissionReportPDF(selectedProfile.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_mission_${selectedProfile.public_name}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Rapport PDF téléchargé avec succès');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du rapport');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className= "min-h-screen bg-gray-50 flex items-center justify-center ">
        <div className= "text-center ">
          <div className= "animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto "></div>
          <p className= "mt-4 text-gray-600 ">Chargement...</p>
        </div>
      </div>
    );
  }

  // If no profiles, redirect to onboarding
  if (profiles.length === 0) {
    return (
      <div className= "min-h-screen bg-gray-50 ">
        <nav className= "bg-white shadow-sm border-b ">
          <div className= "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
            <div className= "flex justify-between h-16 items-center ">
              <div className= "flex items-center gap-2 ">
                <Bandage className= "h-8 w-8 text-blue-600 " />
                <span className= "text-xl font-bold ">AUTO-RÉPUTATION</span>
              </div>
              <Button variant= "ghost " onClick={handleLogout}>
                <LogOut className= "h-4 w-4 mr-2 " />
                Déconnexion
              </Button>
            </div>
          </div>
        </nav>

        <div className= "max-w-4xl mx-auto px-4 py-16 ">
          <Card>
            <CardHeader className= "text-center ">
              <CardTitle>Bienvenue {user?.full_name || user?.email} !</CardTitle>
              <CardDescription>
                Créez votre premier profil de réputation pour commencer
              </CardDescription>
            </CardHeader>
            <CardContent className= "text-center ">
              <div className= "mb-6 ">
                <Bandage className= "h-24 w-24 text-blue-600 mx-auto opacity-20 " />
              </div>
              <Button onClick={() => navigate('/onboarding')} size= "lg " data-testid= "create-profile-btn ">
                <Plus className= "h-5 w-5 mr-2 " />
                Créer mon profil
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className= "min-h-screen bg-gray-50 ">
      {/* Navigation */}
      <nav className= "bg-white shadow-sm border-b ">
        <div className= "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
          <div className= "flex justify-between h-16 items-center ">
            <div className= "flex items-center gap-2 ">
              <Bandage className= "h-8 w-8 text-blue-600 " />
              <span className= "text-xl font-bold ">AUTO-RÉPUTATION</span>
            </div>
            
            <div className= "flex items-center gap-4 ">
              <span className= "text-sm text-gray-600 ">{user?.email}</span>
              {user?.subscription_tier && (
                <Badge variant= "secondary ">{user.subscription_tier}</Badge>
              )}
              {isAdmin && <Badge variant= "destructive ">Admin</Badge>}
              <Button variant= "ghost " onClick={handleLogout}>
                <LogOut className= "h-4 w-4 mr-2 " />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className= "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ">
        <div className= "grid gap-6 lg:grid-cols-4 ">
          {/* Sidebar */}
          <div className= "lg:col-span-1 ">
            <Card>
              <CardHeader>
                <CardTitle className= "text-base ">Navigation</CardTitle>
              </CardHeader>
              <CardContent className= "space-y-2 ">
                <Button
                  variant= "ghost "
                  className= "w-full justify-start "
                  onClick={() => navigate('/dashboard')}
                  data-testid= "dashboard-nav-btn "
                >
                  <LayoutDashboard className= "h-4 w-4 mr-2 " />
                  Tableau de bord
                </Button>
                <Button
                  variant= "ghost "
                  className= "w-full justify-start "
                  onClick={() => navigate('/profile')}
                  data-testid= "profile-nav-btn "
                >
                  <Users className= "h-4 w-4 mr-2 " />
                  Mon profil
                </Button>
                <Button
                  variant= "ghost "
                  className= "w-full justify-start "
                  onClick={() => navigate('/content')}
                  data-testid= "content-nav-btn "
                >
                  <FileText className= "h-4 w-4 mr-2 " />
                  Contenus
                </Button>
                <Button
                  variant= "ghost "
                  className= "w-full justify-start "
                  onClick={() => navigate('/calendar')}
                  data-testid= "calendar-nav-btn "
                >
                  <Calendar className= "h-4 w-4 mr-2 " />
                  Calendrier
                </Button>
                <Button
                  variant= "ghost "
                  className= "w-full justify-start "
                  onClick={() => navigate('/outlets')}
                  data-testid= "outlets-nav-btn "
                >
                  <Globe className= "h-4 w-4 mr-2 " />
                  Débouchés
                </Button>
                {isAdmin && (
                  <Button
                    variant= "ghost "
                    className= "w-full justify-start "
                    onClick={() => navigate('/admin')}
                    data-testid= "admin-nav-btn "
                  >
                    <Settings className= "h-4 w-4 mr-2 " />
                    Administration
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Profile Selector */}
            <Card className= "mt-4 ">
              <CardHeader>
                <CardTitle className= "text-base ">Profils</CardTitle>
              </CardHeader>
              <CardContent className= "space-y-2 ">
                {profiles.map((profile) => (
                  <Button
                    key={profile.id}
                    variant={selectedProfile?.id === profile.id ? 'default' : 'outline'}
                    className= "w-full justify-start "
                    onClick={() => setSelectedProfile(profile)}
                    data-testid={`profile-selector-${profile.id}`}
                  >
                    {profile.public_name}
                  </Button>
                ))}
                <Button
                  variant= "ghost "
                  className= "w-full justify-start "
                  onClick={() => navigate('/onboarding')}
                  data-testid= "add-profile-btn "
                >
                  <Plus className= "h-4 w-4 mr-2 " />
                  Nouveau profil
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Main Dashboard */}
          <div className= "lg:col-span-3 space-y-6 ">
            {/* Header */}
            <div>
              <h1 className= "text-3xl font-bold text-gray-900 " data-testid= "profile-name ">
                {selectedProfile?.public_name}
              </h1>
              <p className= "text-gray-600 mt-1 ">{selectedProfile?.entity_type}</p>
            </div>

            {/* Stats Cards */}
            {dashboardStats && (
              <div className= "grid gap-4 md:grid-cols-2 lg:grid-cols-3 ">
                <Card>
                  <CardHeader className= "flex flex-row items-center justify-between space-y-0 pb-2 ">
                    <CardTitle className= "text-sm font-medium ">Score de réputation</CardTitle>
                    <TrendingUp className= "h-4 w-4 text-blue-600 " />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-3xl font-bold ${getScoreColor(dashboardStats.reputation_score)}`} data-testid= "reputation-score ">
                      {dashboardStats.reputation_score.toFixed(0)}
                    </div>
                    <p className= "text-xs text-gray-500 mt-1 ">sur 100</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className= "flex flex-row items-center justify-between space-y-0 pb-2 ">
                    <CardTitle className= "text-sm font-medium ">Contenus publiés</CardTitle>
                    <CheckCircle2 className= "h-4 w-4 text-green-600 " />
                  </CardHeader>
                  <CardContent>
                    <div className= "text-3xl font-bold " data-testid= "content-published ">
                      {dashboardStats.content_published}
                    </div>
                    <p className= "text-xs text-gray-500 mt-1 ">{dashboardStats.content_pending} en attente</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className= "flex flex-row items-center justify-between space-y-0 pb-2 ">
                    <CardTitle className= "text-sm font-medium ">Backlinks créés</CardTitle>
                    <FileCheck className= "h-4 w-4 text-purple-600 " />
                  </CardHeader>
                  <CardContent>
                    <div className= "text-3xl font-bold " data-testid= "backlinks-created ">
                      {dashboardStats.backlinks_created}
                    </div>
                    <p className= "text-xs text-gray-500 mt-1 ">liens entrants</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Upsell Alert */}
            {upsellSuggestion && (
              <Card className= "border-orange-500 bg-orange-50 ">
                <CardHeader>
                  <div className= "flex items-center gap-2 ">
                    <Award className= "h-5 w-5 text-orange-600 " />
                    <CardTitle className= "text-base text-orange-900 ">🎯 Opportunité détectée</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className= "text-sm text-orange-800 mb-3 ">{upsellSuggestion.message}</p>
                  <div className= "flex items-center justify-between ">
                    <div>
                      <Badge variant= "outline " className= "mr-2 ">{upsellSuggestion.current_tier}</Badge>
                      <span className= "text-sm ">→</span>
                      <Badge className= "ml-2 bg-orange-600 ">{upsellSuggestion.suggested_tier}</Badge>
                    </div>
                    <Button size= "sm " variant= "default " onClick={() => navigate('/billing')}>
                      Voir les options
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mission Tracking & Profitability */}
            {missionTracking && (
              <Card>
                <CardHeader>
                  <div className= "flex items-center justify-between ">
                    <div>
                      <CardTitle>Suivi de Mission & Rentabilité</CardTitle>
                      <CardDescription>
                        Formule: <Badge variant= "secondary ">{missionTracking.subscription_tier}</Badge> · 
                        {missionTracking.days_elapsed} jours / {missionTracking.days_elapsed + missionTracking.days_remaining} jours
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={handleDownloadPDF} 
                      disabled={downloadingPDF}
                      size= "sm "
                    >
                      <Download className= "h-4 w-4 mr-2 " />
                      {downloadingPDF ? 'Génération...' : 'Télécharger Rapport PDF'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className= "grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4 ">
                    <div className= "space-y-1 ">
                      <div className= "flex items-center gap-1 text-sm text-gray-600 ">
                        <Euro className= "h-4 w-4 " />
                        <span>Budget</span>
                      </div>
                      <div className= "text-2xl font-bold ">{missionTracking.budget_used.toFixed(0)}€</div>
                      <p className= "text-xs text-gray-500 ">
                        sur {missionTracking.budget_total.toFixed(0)}€ ({missionTracking.budget_progress.toFixed(0)}%)
                      </p>
                    </div>

                    <div className= "space-y-1 ">
                      <div className= "flex items-center gap-1 text-sm text-gray-600 ">
                        <Target className= "h-4 w-4 " />
                        <span>Publications</span>
                      </div>
                      <div className= "text-2xl font-bold ">{missionTracking.completed_publications}</div>
                      <p className= "text-xs text-gray-500 ">
                        sur {missionTracking.target_publications} ({missionTracking.content_progress.toFixed(0)}%)
                      </p>
                    </div>

                    <div className= "space-y-1 ">
                      <div className= "flex items-center gap-1 text-sm text-gray-600 ">
                        <TrendingUp className= "h-4 w-4 " />
                        <span>Progression</span>
                      </div>
                      <div className= "text-2xl font-bold ">{missionTracking.progress_percentage.toFixed(0)}%</div>
                      <p className= "text-xs text-gray-500 ">globale</p>
                    </div>

                    <div className= "space-y-1 ">
                      <div className= "flex items-center gap-1 text-sm text-gray-600 ">
                        <Euro className= "h-4 w-4 " />
                        <span>Coût moyen</span>
                      </div>
                      <div className= "text-2xl font-bold ">{missionTracking.avg_cost_per_publication.toFixed(0)}€</div>
                      <p className= "text-xs text-gray-500 ">par publication</p>
                    </div>
                  </div>

                  <div className= "border-t pt-4 ">
                    <h4 className= "text-sm font-semibold mb-3 ">Distribution par priorité</h4>
                    <div className= "grid grid-cols-4 gap-2 text-center ">
                      <div className= "space-y-1 ">
                        <Badge variant= "destructive ">S</Badge>
                        <div className= "text-lg font-bold ">{missionTracking.publications_priority_s}</div>
                        <p className= "text-xs text-gray-500 ">Premium</p>
                      </div>
                      <div className= "space-y-1 ">
                        <Badge variant= "default ">A</Badge>
                        <div className= "text-lg font-bold ">{missionTracking.publications_priority_a}</div>
                        <p className= "text-xs text-gray-500 ">Owned</p>
                      </div>
                      <div className= "space-y-1 ">
                        <Badge variant= "secondary ">B</Badge>
                        <div className= "text-lg font-bold ">{missionTracking.publications_priority_b}</div>
                        <p className= "text-xs text-gray-500 ">Network</p>
                      </div>
                      <div className= "space-y-1 ">
                        <Badge variant= "outline ">C</Badge>
                        <div className= "text-lg font-bold ">{missionTracking.publications_priority_c}</div>
                        <p className= "text-xs text-gray-500 ">External</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
                <CardDescription>Gérez votre réputation efficacement</CardDescription>
              </CardHeader>
              <CardContent className= "grid gap-4 md:grid-cols-2 ">
                <Button onClick={() => navigate('/strategy')} data-testid= "generate-strategy-btn ">
                  <TrendingUp className= "h-4 w-4 mr-2 " />
                  Générer une stratégie
                </Button>
                <Button variant= "outline " onClick={() => navigate('/content/new')} data-testid= "create-content-btn ">
                  <FileText className= "h-4 w-4 mr-2 " />
                  Créer du contenu
                </Button>
                <Button variant= "outline " onClick={() => navigate('/proof')} data-testid= "add-proof-btn ">
                  <Bandage className= "h-4 w-4 mr-2 " />
                  Ajouter une preuve
                </Button>
                <Button variant= "outline " onClick={() => navigate('/billing')} data-testid= "billing-btn ">
                  <Settings className= "h-4 w-4 mr-2 " />
                  Facturation
                </Button>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            {dashboardStats?.recent_activity && dashboardStats.recent_activity.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Activité récente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className= "space-y-4 ">
                    {dashboardStats.recent_activity.slice(0, 5).map((activity) => (
                      <div key={`${activity.created_at}-${activity.action}`} className= "flex items-center gap-3 text-sm ">
                        <div className= "h-2 w-2 rounded-full bg-blue-600 "></div>
                        <span className= "text-gray-600 ">{activity.action}</span>
                        <span className= "text-gray-400 text-xs ml-auto ">
                          {new Date(activity.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
